"use client";

import { useState } from "react";
import { trackLead } from "@/lib/analytics";

interface Props {
  slug: string;
  recipeName: string;
}

export function RecipeEmailCta({ slug, recipeName }: Props) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError(null);
    try {
      // Verdibyttet (gratis ukesmeny + løpende oppskrifter/tips) er tydelig
      // opplyst ved knappen, så innsending er informert, aktivt samtykke.
      const res = await fetch("/api/recipes/email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name, slug, marketing: true }),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.error || "Noe gikk galt");
      } else {
        setDone(true);
        trackLead("recipe_email_signup", { email, recipe_slug: slug, magnet: "ukesmeny_500kcal" });
      }
    } catch {
      setError("Kunne ikke sende e-post. Prøv igjen om litt.");
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <div className="mt-10 rounded-3xl border border-green-200/60 bg-gradient-to-br from-green-50 via-emerald-50/70 to-amber-50 p-7 sm:p-9 shadow-sm text-center">
        <div className="flex justify-center mb-4" aria-hidden="true">
          <div className="w-12 h-12 rounded-full bg-green-500/15 flex items-center justify-center">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M5 13l4 4L19 7" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
        <h3 className="text-lg font-semibold text-stone-900 mb-1.5">Ukesmenyen er på vei!</h3>
        <p className="text-sm text-stone-600 max-w-md mx-auto">
          Sjekk innboksen — kanskje også søppelposten første gang. Du får 7 middager under 500 kcal og «{recipeName}» ferdig formatert.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-10 rounded-3xl border border-stone-200/70 bg-gradient-to-br from-amber-50/80 via-orange-50/40 to-stone-50 p-7 sm:p-9 shadow-sm relative overflow-hidden">
      <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-gradient-to-br from-amber-200/40 to-orange-200/20 blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/80 border border-stone-200/60 shadow-sm" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" stroke="#57534e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className="text-xs font-medium text-stone-700 uppercase tracking-wide">Gratis ukesmeny</span>
        </div>
        <h3 className="text-xl sm:text-2xl font-bold text-stone-900 leading-tight mb-2">
          Få 7 middager under 500 kcal <span className="italic text-stone-500 font-normal">gratis på e-post</span>
        </h3>
        <p className="text-sm text-stone-600 mb-5 max-w-lg">
          En ferdig ukesmeny som PDF — med handleliste og næringsinnhold — pluss «{recipeName}» ferdig formatert. Perfekt om du vil ned i vekt uten å telle hver eneste kalori.
        </p>

        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="Fornavn (valgfritt)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-white/80 backdrop-blur-sm border border-stone-200/70 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-stone-400 placeholder:text-stone-400"
            />
            <input
              type="email"
              required
              placeholder="din@epost.no"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white/80 backdrop-blur-sm border border-stone-200/70 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-stone-400 placeholder:text-stone-400"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-2.5 items-stretch sm:items-center pt-1">
            <button
              type="submit"
              disabled={!email || loading}
              className="px-6 py-3 bg-stone-900 text-white rounded-xl text-sm font-semibold hover:bg-stone-800 transition-all shadow-sm shadow-stone-900/10 disabled:opacity-50 disabled:cursor-not-allowed flex-1 sm:flex-initial"
            >
              {loading ? "Sender …" : "Send meg ukesmenyen →"}
            </button>
            <p className="text-xs text-stone-400 sm:ml-3">Vi deler aldri e-posten din.</p>
          </div>

          <p className="text-xs text-stone-500 leading-relaxed pt-0.5 max-w-lg">
            Du får ukesmenyen med én gang — pluss nye oppskrifter og kostholds-tips fra CoachGuiden. Du kan melde deg av når som helst.
          </p>
        </form>

        {error && (
          <div className="mt-3 rounded-xl bg-red-50 border border-red-200 px-3.5 py-2.5 text-xs text-red-700">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
